/**
 * Maps Trimble/AutoCAD SHX stroke font names (as stored in TypefaceName)
 * to visually similar CSS font-family stacks for browser rendering.
 *
 * SHX fonts are single-stroke vector fonts that don't exist in browsers.
 * These mappings pick the closest available web-safe font to approximate
 * the stroke font's visual character.
 */

type StrokeFontEntry = {
  readonly css: string;
  readonly label: string;
};

const STROKE_FONT_MAP: ReadonlyMap<string, StrokeFontEntry> = new Map([
  // === Simple / Sans-serif stroke fonts ===
  ['txt', { css: "'Courier New', Courier, monospace", label: 'txt' }],
  ['simplex', { css: "'Courier New', Courier, monospace", label: 'simplex' }],
  ['monotxt', { css: "'Courier New', Courier, monospace", label: 'monotxt' }],

  // === Roman (serifed) stroke fonts ===
  ['romans', { css: "'Times New Roman', Times, serif", label: 'romans' }],
  ['romanc', { css: "'Times New Roman', Times, serif", label: 'romanc' }],
  ['romand', { css: "'Times New Roman', Times, serif", label: 'romand' }],
  ['romant', { css: "'Times New Roman', Times, serif", label: 'romant' }],

  // === Complex stroke fonts ===
  ['complex', { css: "'Times New Roman', Times, serif", label: 'complex' }],

  // === Gothic stroke fonts ===
  ['gothice', { css: 'Arial, Helvetica, sans-serif', label: 'gothice' }],
  ['gothicg', { css: 'Arial, Helvetica, sans-serif', label: 'gothicg' }],
  ['gothici', { css: 'Arial, Helvetica, sans-serif', label: 'gothici' }],

  // === Italic stroke fonts ===
  ['italic', { css: "'Times New Roman', Times, serif", label: 'italic' }],
  ['italicc', { css: "'Times New Roman', Times, serif", label: 'italicc' }],
  ['italict', { css: "'Times New Roman', Times, serif", label: 'italict' }],

  // === Script stroke fonts ===
  ['scripts', { css: "'Comic Sans MS', cursive", label: 'scripts' }],
  ['scriptc', { css: "'Comic Sans MS', cursive", label: 'scriptc' }],

  // === Greek stroke fonts ===
  ['greeks', { css: "'Times New Roman', Times, serif", label: 'greeks' }],
  ['greekc', { css: "'Times New Roman', Times, serif", label: 'greekc' }],

  // === ISO stroke fonts ===
  ['isocp', { css: 'Arial, Helvetica, sans-serif', label: 'isocp' }],
  ['isocp2', { css: 'Arial, Helvetica, sans-serif', label: 'isocp2' }],
  ['isocp3', { css: 'Arial, Helvetica, sans-serif', label: 'isocp3' }],
  ['isoct', { css: 'Arial, Helvetica, sans-serif', label: 'isoct' }],
  ['isoct2', { css: 'Arial, Helvetica, sans-serif', label: 'isoct2' }],
  ['isoct3', { css: 'Arial, Helvetica, sans-serif', label: 'isoct3' }],

  // === Architectural stroke fonts ===
  ['archchis', { css: 'Arial, Helvetica, sans-serif', label: 'archchis' }],
  ['archpres', { css: 'Arial, Helvetica, sans-serif', label: 'archpres' }],
  ['archquik', { css: 'Arial, Helvetica, sans-serif', label: 'archquik' }],
  ['archsing', { css: 'Arial, Helvetica, sans-serif', label: 'archsing' }],

  // === Symbol font ===
  ['symbol', { css: 'Symbol, serif', label: 'symbol' }],

  // === GDT (geometric dimensioning) ===
  ['gdt', { css: 'Symbol, serif', label: 'gdt' }],

  // === Decorative / display stroke fonts ===
  ['amtypenh', { css: 'Arial, Helvetica, sans-serif', label: 'amtypenh' }],
  ['amtypenl', { css: 'Arial, Helvetica, sans-serif', label: 'amtypenl' }],
  ['amtypeno', { css: 'Arial, Helvetica, sans-serif', label: 'amtypeno' }],

  ['avongrnh', { css: "'Times New Roman', Times, serif", label: 'avongrnh' }],
  ['avongrnl', { css: "'Times New Roman', Times, serif", label: 'avongrnl' }],
  ['avongrno', { css: "'Times New Roman', Times, serif", label: 'avongrno' }],

  ['bolonybh', { css: 'Arial, Helvetica, sans-serif', label: 'bolonybh' }],
  ['bolonybl', { css: 'Arial, Helvetica, sans-serif', label: 'bolonybl' }],
  ['bolonybo', { css: 'Arial, Helvetica, sans-serif', label: 'bolonybo' }],
  ['bolonyhh', { css: 'Arial, Helvetica, sans-serif', label: 'bolonyhh' }],
  ['bolonyhl', { css: 'Arial, Helvetica, sans-serif', label: 'bolonyhl' }],
  ['bolonyho', { css: 'Arial, Helvetica, sans-serif', label: 'bolonyho' }],

  ['multplnh', { css: 'Arial, Helvetica, sans-serif', label: 'multplnh' }],
  ['multplnl', { css: 'Arial, Helvetica, sans-serif', label: 'multplnl' }],
  ['multplno', { css: 'Arial, Helvetica, sans-serif', label: 'multplno' }],

  ['peanutnh', { css: "'Comic Sans MS', cursive", label: 'peanutnh' }],
  ['peanutnl', { css: "'Comic Sans MS', cursive", label: 'peanutnl' }],
  ['peanutno', { css: "'Comic Sans MS', cursive", label: 'peanutno' }],

  ['technqnh', { css: "'Courier New', Courier, monospace", label: 'technqnh' }],
  ['technqnl', { css: "'Courier New', Courier, monospace", label: 'technqnl' }],
  ['technqno', { css: "'Courier New', Courier, monospace", label: 'technqno' }],

  ['tmodelf', { css: 'Arial, Helvetica, sans-serif', label: 'tmodelf' }],
  ['tmodelp', { css: 'Arial, Helvetica, sans-serif', label: 'tmodelp' }],

  ['velvetbh', { css: "'Times New Roman', Times, serif", label: 'velvetbh' }],
  ['velvetbl', { css: "'Times New Roman', Times, serif", label: 'velvetbl' }],
  ['velvetbo', { css: "'Times New Roman', Times, serif", label: 'velvetbo' }],
  ['velvethh', { css: "'Times New Roman', Times, serif", label: 'velvethh' }],
  ['velvethl', { css: "'Times New Roman', Times, serif", label: 'velvethl' }],
  ['velvetho', { css: "'Times New Roman', Times, serif", label: 'velvetho' }],
  ['velvetnh', { css: "'Times New Roman', Times, serif", label: 'velvetnh' }],
  ['velvetnl', { css: "'Times New Roman', Times, serif", label: 'velvetnl' }],
  ['velvetno', { css: "'Times New Roman', Times, serif", label: 'velvetno' }],
]);

/**
 * Normalizes a typeface name from the backend to a lookup key.
 * Strips `.fnt`, `.shx`, `.shp` extensions and lowercases.
 */
function normalizeTypefaceName(typefaceName: string): string {
  return typefaceName
    .trim()
    .toLowerCase()
    .replace(/\.(fnt|shx|shp)$/i, '');
}

/**
 * Returns the CSS font-family for a given CAD typeface name, or `undefined`
 * if the typeface is not a known stroke font.
 */
export function resolveStrokeFontCss(
  typefaceName: string | undefined
): string | undefined {
  if (!typefaceName?.trim()) {
    return undefined;
  }
  return STROKE_FONT_MAP.get(normalizeTypefaceName(typefaceName))?.css;
}

/** Returns true when the typeface name maps to a known SHX/FNT stroke font. */
export function isStrokeFont(typefaceName: string | undefined): boolean {
  if (!typefaceName?.trim()) {
    return false;
  }
  return STROKE_FONT_MAP.has(normalizeTypefaceName(typefaceName));
}

/**
 * Common SHX stroke font names for the properties panel font dropdown.
 * Only includes the most commonly used fonts, not the full set.
 */
export const STROKE_FONT_OPTIONS: readonly string[] = [
  'txt',
  'simplex',
  'complex',
  'monotxt',
  'romans',
  'romanc',
  'romand',
  'romant',
  'gothice',
  'gothicg',
  'gothici',
  'italic',
  'italicc',
  'italict',
  'scripts',
  'scriptc',
  'isocp',
  'isocp2',
  'isocp3',
  'isoct',
  'isoct2',
  'isoct3',
];
